import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  UntypedFormArray,
  UntypedFormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { SelectItem } from '@openng/optimus-ui/api';
import { firstValueFrom, forkJoin, interval, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  DELETE_SHIFT_TURNOVER,
  VIEW_AUDIT,
} from 'src/app/constants/action-constants';
import {
  MC_ST_DETAIL_CONTAINER,
  MC_ST_PRINT_CONTAINER,
} from 'src/app/constants/common.constants';
import {
  DEPARTING_COMPLETED,
  INCOMING_COMPLETED,
  PRINT_SHIFT_TURNOVER,
} from 'src/app/constants/crm-constants';
import {
  MC_ST_TAB_PREFIX,
  NEW_MC_SHIFT_TURNOVER,
} from 'src/app/constants/monlog-constants';
import { DetailsContainer } from 'src/app/core/containers/details-container';
import {
  clearFormArray,
  dynamicSort,
  dynamicSubfieldSort,
  getDirtyValues,
} from 'src/app/core/functions/common-functions';
import { DirtyStatus } from 'src/app/model/common/dirty-status';
import { Contact } from 'src/app/model/contacts/contact';
import {
  ShiftTurnover,
  UpdateShiftTurnoverInput,
} from 'src/app/model/shift/shift-turnover';
import { AuthService } from 'src/app/services/auth.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { FacilityBuilderService } from 'src/app/services/builder/facility-builder.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { ShiftTurnoverApiService } from 'src/app/services/api/shift-turnover-api.service';
import { ShiftTurnoverBuilderService } from 'src/app/services/builder/shift-turnover-builder.service';
import { isNullOrUndefined } from 'src/app/utils/utils';
import moment from 'moment-mini';
import { ShiftTurnoverTableService } from 'src/app/services/table/shift-turnover-table.service';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { SelectChangeEvent, Select } from '@openng/optimus-ui/select';
import {
  AutoCompleteCompleteEvent,
  AutoComplete,
} from '@openng/optimus-ui/autocomplete';
import { DialogService } from '@openng/optimus-ui/dynamicdialog';
import { AuditDialogContainerComponent } from 'src/app/core/containers/audit-dialog-container.component';
import { ProgressSpinnerComponent } from '../../core/components/progress-spinner.component';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { DatePicker } from '@openng/optimus-ui/datepicker';
import { InputText } from '@openng/optimus-ui/inputtext';
import { Ripple } from '@openng/optimus-ui/ripple';
import { ToggleButton } from '@openng/optimus-ui/togglebutton';
import { Textarea } from '@openng/optimus-ui/textarea';
import { InlineValidationComponent } from '../../core/components/inline-validation.component';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { Panel } from '@openng/optimus-ui/panel';
import {
  ShiftTurnoverMessageKeys,
  ValidationMessageEntry,
  ValidationParamEntry,
} from 'src/app/core/type-helpers/validation-msg';
import { ShiftGroup } from 'src/app/model/shift/shift-group';
import { BlockableDiv } from 'src/app/common/components/blockable-div/blockable-div.component';

@Component({
  selector: 'app-mc-shift-turnover-detail',
  templateUrl: './mc-shift-turnover-detail.component.html',
  styleUrls: ['./mc-shift-turnover-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ProgressSpinnerComponent,
    BlockUI,
    FormsModule,
    ReactiveFormsModule,
    Select,
    ButtonDirective,
    DatePicker,
    InputText,
    AutoComplete,
    Ripple,
    ToggleButton,
    Textarea,
    InlineValidationComponent,
    TranslateDirective,
    Panel,
    TranslatePipe,
    BlockableDiv,
  ],
})
export class McShiftTurnoverDetailComponent
  extends DetailsContainer
  implements OnInit, OnDestroy, AfterContentChecked
{
  protected _fb = inject(UntypedFormBuilder);
  protected _cdRef = inject(ChangeDetectorRef);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _facilityBuilder = inject(FacilityBuilderService);
  protected _shiftTurnoverApi = inject(ShiftTurnoverApiService);
  protected _shiftTurnoverTable = inject(ShiftTurnoverTableService);
  protected _shiftTurnoverBuilder = inject(ShiftTurnoverBuilderService);
  protected _auth = inject(AuthService);
  protected _authApi = inject(AuthApiService);
  protected _dialogService = inject(DialogService);

  shiftTurnover: ShiftTurnover | null = null;
  isEditting = false;
  filteredContacts: any[] = [];
  showEntryDialog = false;
  saving = false;
  actions: SelectItem[] = [];

  addIcon = 'fa fa-fw fa-plus';
  updateIcon = 'fa fa-fw fa-save';
  isUnavailable = false;
  groups: ShiftGroup[] = [];
  shiftTypes: SelectItem[] = [];
  textAreaSize = 62;
  deviceSub?: Subscription;
  activeTabChangedSub!: Subscription;
  refreshSubscription?: Subscription;
  backgroundRefreshPaused: boolean = false;
  selectedAction: any = null;
  canEditResolved = false;
  canEditUnresolved = false;
  canCreate = false;
  canViewDeparting = false;
  canViewIncoming = false;
  previousTurnoverStatus = '';

  validationMessages?: Record<ShiftTurnoverMessageKeys, ValidationMessageEntry>;
  validationParams?: Partial<
    Record<ShiftTurnoverMessageKeys, ValidationParamEntry>
  >;

  ngOnInit() {
    this.canViewDeparting = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.MONITORING_CENTER.MC_SHIFT_TURNOVERS
        .VIEW_ALL_MC_TURNOVER_DEPARTING_SECTIONS,
    ]);
    this.canViewIncoming = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.MONITORING_CENTER.MC_SHIFT_TURNOVERS
        .VIEW_ALL_MC_TURNOVER_INCOMING_SECTIONS,
    ]);

    const validations = this._shiftTurnoverBuilder.buildValidationMessages();
    this.validationMessages = validations.messages;
    this.validationParams = validations.params;

    this.setupTabClosingSubscription();
    this.setupActiveTabChangedSubscription();
    const refreshInterval = interval(60000);
    this.refreshSubscription = refreshInterval.subscribe(() => {
      this.performBackgroundRefresh();
    });
    this.initShiftTurnover();
  }

  ngOnDestroy(): void {
    if (this.deviceSub) {
      this.deviceSub.unsubscribe();
    }

    if (this.tabClosingSub) {
      this.tabClosingSub.unsubscribe();
    }

    if (this.valueSub) {
      this.valueSub.unsubscribe();
    }
  }

  ngAfterContentChecked(): void {
    const activeId = this.id;
    if (
      !isNullOrUndefined(activeId) &&
      this.shiftTurnover &&
      this.shiftTurnover.id !== activeId &&
      !this.doCheckComplete
    ) {
      this.doCheckComplete = true;
      this._shiftTurnoverTable.setSelected(null);
      this.initShiftTurnover();
    }
  }

  checkDeviceSize() {
    if (this._deviceService.isMobile()) {
      this.textAreaSize = 42;
    } else {
      this.textAreaSize = 62;
    }
  }

  initShiftTurnover() {
    this.deviceSub = this._deviceService.mediaChangeEvent.subscribe((event) => {
      this.checkDeviceSize();
    });
    this.checkDeviceSize();

    const id = this.id;
    if (id) {
      this._shiftTurnoverApi.getShiftTurnover(id).subscribe((turnover) => {
        this.shiftTurnover = turnover;
        this.doCheckComplete = false;
        this.loadShiftData();
      });
    } else {
      this.shiftTurnover = this._shiftTurnoverTable.getSelected();
      if (this.shiftTurnover) {
        this.isEditting = true;
        // Creating new turnover, we want to default to the current user for departing
        this._contactApi
          .getContactByEmail(this._auth.getEmail()!)
          .pipe(take(1))
          .subscribe((data: Contact) => {
            const id = data.id;
            this.form?.patchValue({
              departingAnalyst: {
                id: id,
                fullName: data.firstName + ' ' + data.lastName,
              },
            });
            this.form?.controls['departingAnalyst'].markAsDirty();
          });
      } else {
        this._logAndMessage.errorLogOnly('MC shift turnover was null.');
        const index = this._tabService.getActiveIndex();
        this._tabService.closeTab(index);
      }
      this.loadShiftData();
    }
  }

  loadShiftData() {
    if (!this.canViewDeparting || !this.canViewIncoming) {
      this._contactApi
        .getContactByEmail(this._auth.getEmail()!)
        .pipe(take(1))
        .subscribe((data: Contact) => {
          const id = data.id;
          this.shiftTurnover?.ShiftDetails.forEach((s) => {
            if (s.incomingDepartingFlag === 'D' && s.analystId === id) {
              this.canViewDeparting = true;
            }

            if (s.incomingDepartingFlag === 'I' && s.analystId === id) {
              this.canViewIncoming = true;
            }
          });
        });
    }

    this._shiftTurnoverApi.getShiftGroups().subscribe((groups) => {
      this.groups = groups;
    });

    this._shiftTurnoverApi.getShiftTypes().subscribe((types) => {
      this.shiftTypes = [
        { label: 'Choose', value: null },
        ...types
          .filter((d) => d.ShiftGroup.name === 'Monitoring Center')
          .map((d) => ({ label: d.name, value: d.id })),
      ];
      this.initialize();
    });
  }

  async initialize() {
    this.canEditResolved = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.MONITORING_CENTER.MC_SHIFT_TURNOVERS.EDIT_MC_RESOLVED_TURNOVERS,
    ]);
    this.canEditUnresolved = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.MONITORING_CENTER.MC_SHIFT_TURNOVERS.EDIT_MC_UNRESOLVED_TURNOVERS,
    ]);
    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.MONITORING_CENTER.MC_SHIFT_TURNOVERS.CREATE_MC_TURNOVERS,
    ]);

    const labels = await firstValueFrom(
      forkJoin({
        new: this._translateService.get('MONLOG.LABEL.NEW_SHIFT_TURNOVER'),
        regularPrefix: this._translateService.get('MONLOG.LABEL.MC_TURNOVER'),
      })
    );

    if (!this.shiftTurnover?.id) {
      this._tabService.updateActiveTabLabel(labels.new);
      this.loading = false;
    } else {
      const header =
        labels.regularPrefix +
        ': ' +
        this.shiftTurnover.shift +
        ' - ' +
        moment(this.shiftTurnover.shiftDate).format('MM/DD/YYYY').toString();
      this.screenName = header;
      const tab = this._tabService.getTabById(this.shiftTurnover.id);
      this._tabService.updateTabLabel(tab!, header);
      this.loading = false;
    }

    this.buildForm();
    this.updateForm();
    this.updateActionsList();
  }

  buildForm() {
    this.form = this._fb.group({
      incomingAnalyst: [null, Validators.required],
      departingAnalyst: [null, Validators.required],
      shiftType: [null, Validators.required],
      shiftDate: [null, Validators.required],
      createdDt: [{ value: null, disabled: true }],
      status: [{ value: null, disabled: true }],
      incomingAdditionalComments: [
        null,
        this._shiftTurnoverBuilder.commentsTooLongValidator(),
      ],
      departingAdditionalComments: [
        null,
        this._shiftTurnoverBuilder.commentsTooLongValidator(),
      ],
      departingFitFlag: [null],
      incomingFitFlag: [null],
      incomingReviewedAllSections: [null],
      items: this._fb.array([]),
      mitigations: this._fb.array([]),
      businessUnit: [null],
      departingCompleted: [{ value: null, disabled: true }],
      incomingCompleted: [{ value: null, disabled: true }],
    });

    this.valueSub = this.form.valueChanges.subscribe((changes) => {
      if (this.form?.dirty) {
        this.changesEvent.emit({
          isDirty: true,
          index: this.index(),
          id: this.id,
        });
      }
    });
  }

  updateForm() {
    const inArray = this.shiftTurnover?.ShiftDetails.filter(
      (d) => d.incomingDepartingFlag === 'I'
    );
    const depArray = this.shiftTurnover?.ShiftDetails.filter(
      (d) => d.incomingDepartingFlag === 'D'
    );
    let statusValue = 'New';
    if (this.shiftTurnover?.status && this.shiftTurnover.status === 'DC') {
      statusValue = 'Departing Complete';
    } else if (
      this.shiftTurnover?.status &&
      this.shiftTurnover.status === 'R'
    ) {
      statusValue = 'Resolved';
    }

    this.form?.patchValue({
      incomingAnalyst:
        (inArray?.length ?? -1 > 0)
          ? {
              id: inArray![0].Analyst!.id,
              fullName:
                inArray![0].Analyst!.firstName +
                ' ' +
                inArray![0].Analyst!.lastName,
            }
          : null,
      departingAnalyst:
        (depArray?.length ?? -1 > 0)
          ? {
              id: depArray![0].Analyst!.id,
              fullName:
                depArray![0].Analyst!.firstName +
                ' ' +
                depArray![0].Analyst!.lastName,
            }
          : null,
      shiftType: this.shiftTurnover?.ShiftType
        ? this.shiftTurnover.ShiftType.id
        : null,
      shiftDate: this.shiftTurnover?.shiftDate,
      status: statusValue,
      createdDt: this.shiftTurnover?.createdAt,
      businessUnit: null,
      departingCompleted: this.shiftTurnover?.departingCompletedDate,
      incomingCompleted: this.shiftTurnover?.incomingCompletedDate,
    });

    let incoming,
      departing = null;
    if (
      this.shiftTurnover?.ShiftDetails &&
      this.shiftTurnover.ShiftDetails.length > 0
    ) {
      incoming = this.shiftTurnover.ShiftDetails.filter(
        (d) => d.incomingDepartingFlag === 'I'
      )[0];
      departing = this.shiftTurnover.ShiftDetails.filter(
        (d) => d.incomingDepartingFlag === 'D'
      )[0];
      this.form?.patchValue({
        incomingAdditionalComments: incoming.additionalComments,
        incomingFitFlag: incoming.fitFlag,
        incomingReviewedAllSections: incoming.reviewedAllSections,
        departingAdditionalComments: departing.additionalComments,
        departingFitFlag: departing.fitFlag,
      });

      const depItemArray = departing.ShiftTurnoverItems.filter(
        (co: any) => co.dirtyStatus !== DirtyStatus.DELETED
      ).sort(dynamicSubfieldSort('ShiftItem', 'sortOrder', 1));

      const itemsArray = this.form?.get('items') as UntypedFormArray;
      clearFormArray(itemsArray);
      for (let i = 0; i < depItemArray.length; i++) {
        const d = depItemArray[i];
        itemsArray.push(
          this._fb.group({
            departingId: d.id,
            name: d.ShiftItem?.name,
            departingShiftItemId: d.ShiftItem?.id,
            departingComments: [
              d.comments,
              this._shiftTurnoverBuilder.commentsTooLongValidator(),
            ],
            departingTurnoverFlag: d.turnoverFlag,
          })
        );
      }

      const depMitArray = departing.ShiftTurnoverMitigations.filter(
        (co: any) => co.dirtyStatus !== DirtyStatus.DELETED
      ).sort(dynamicSubfieldSort('ShiftMitigation', 'sortOrder', 1));
      const mitArray = this.form?.get('mitigations') as UntypedFormArray;
      clearFormArray(mitArray);
      for (let j = 0; j < depMitArray.length; j++) {
        const mit = depMitArray[j];
        mitArray.push(
          this._fb.group({
            id: mit.id,
            mitigationId: mit.ShiftMitigation?.id,
            mitigationName: mit.ShiftMitigation?.name,
            turnoverFlag: mit.turnoverFlag,
          })
        );
      }
    }

    this.form?.markAsPristine();

    if (!this.isEditting) {
      this.form?.disable();
    } else {
      this.setEditting();
    }
  }

  checkAnalystCompleted() {
    if (
      this.shiftTurnover &&
      !this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.MONITORING_CENTER.MC_SHIFT_TURNOVERS.EDIT_LOCKED_MC_TURNOVERS,
      ])
    ) {
      if (this.shiftTurnover.incomingCompletedDate != null) {
        const groups = (this.form?.controls.items as FormArray).controls;
        groups.forEach((group: AbstractControl) => {
          let incomingComments = group.get('incomingComments');
          let incomingTurnoverFlags = group.get('incomingTurnoverFlag');
          incomingComments?.disable();
          incomingTurnoverFlags?.disable();
        });
        // The Additional Comments field in the Departing/Incoming sections were flipped previously.
        // Since we don't know how long these have been like this, switching the fields back would
        // cause the old mon logs to display information incorrectly.
        this.form?.get('departingAdditionalComments')?.disable();
        this.form?.get('incomingFitFlag')?.disable();
        this.form?.get('incomingReviewedAllSections')?.disable();
      }

      if (this.shiftTurnover.departingCompletedDate != null) {
        const groups = (this.form?.controls.items as FormArray).controls;
        groups.forEach((group: AbstractControl) => {
          let departingComments = group.get('departingComments');
          let departingTurnoverFlags = group.get('departingTurnoverFlag');
          departingComments?.disable();
          departingTurnoverFlags?.disable();
        });
        this.form?.get('mitigations')?.disable();
        this.form?.get('incomingAdditionalComments')?.disable();
        this.form?.get('departingFitFlag')?.disable();
      }
    }
  }

  async setEditting() {
    if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.MONITORING_CENTER.MC_SHIFT_TURNOVERS
          .EDIT_ALL_MC_TURNOVER_SECTIONS
      )
    ) {
      this.form?.enable();
    } else if (
      this._authApi.doesUserHaveClaim(
        CLAIMS.MONITORING_CENTER.MC_SHIFT_TURNOVERS
          .EDIT_OWN_MC_TURNOVER_SECTIONS
      )
    ) {
      this.form?.enable();
      this.loading = true;
      // determine if the user is either the departing or incoming & only enable those sections
      const contact = await firstValueFrom(
        this._contactApi.getContactByEmail(this._auth.getEmail()!)
      );
      if (!this.shiftTurnover) {
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'CRM.MESSAGES.HEADERS.UNEXPECTED_ERROR',
          bodyKey: 'CRM.MESSAGES.ERROR.UNEXPECTED_ERROR',
        });
        console.error(
          'Somehow user made it to set editting with no shift turnover!'
        );
        return;
      }

      const departingDetail = this.shiftTurnover.ShiftDetails?.filter(
        (sd) => sd.incomingDepartingFlag === 'D'
      )[0];
      const isDeparting = departingDetail?.analystId === contact.id;
      if (
        !isDeparting ||
        (isDeparting && this.shiftTurnover.departingCompletedDate)
      ) {
        console.log('Disabling departing fields');
        console.log('User is departing:', isDeparting);
        console.log(
          'Shift Turnover Departing Completed Date:',
          this.shiftTurnover.departingCompletedDate
        );
        const groups = (this.form?.controls.items as FormArray).controls;
        groups.forEach((group: AbstractControl) => {
          let departingComments = group.get('departingComments');
          let departingTurnoverFlags = group.get('departingTurnoverFlag');
          departingComments?.disable();
          departingTurnoverFlags?.disable();
        });
        this.form?.get('mitigations')?.disable();
        this.form?.get('incomingAdditionalComments')?.disable();
        this.form?.get('departingFitFlag')?.disable();
      }

      const incoming = this.shiftTurnover.ShiftDetails?.filter(
        (sd) => sd.incomingDepartingFlag === 'I'
      )[0];
      const isIncoming = incoming?.analystId === contact.id;
      if (
        !isIncoming ||
        (isIncoming && !this.shiftTurnover.departingCompletedDate)
      ) {
        console.log('Disabling Incoming fields');
        console.log('User is incoming:', isIncoming);
        console.log(
          'Shift Turnover Departing Completed Date:',
          this.shiftTurnover.departingCompletedDate
        );
        const groups = (this.form?.controls.items as FormArray).controls;
        groups.forEach((group: AbstractControl) => {
          let incomingComments = group.get('incomingComments');
          let incomingTurnoverFlags = group.get('incomingTurnoverFlag');
          incomingComments?.disable();
          incomingTurnoverFlags?.disable();
        });
        this.form?.get('departingAdditionalComments')?.disable();
        this.form?.get('incomingFitFlag')?.disable();
        this.form?.get('incomingReviewedAllSections')?.disable();
      }

      this.loading = false;

      // If we haven't actually created the turnover yet we want to make sure not to disable
      // the form otherwise we will prevent all creations
      const isInitialCreate = this.shiftTurnover.ShiftDetails.length === 0;
      const userIsOnThisTurnover = isDeparting || isIncoming;

      if (!userIsOnThisTurnover && !isInitialCreate) {
        this.isEditting = false;
        this.form?.disable();
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'CRM.MESSAGES.HEADERS.NO_TURNOVER_EDIT_PERMISSIONS',
          bodyKey: 'CRM.MESSAGES.ERROR.NO_TURNOVER_EDIT_PERMISSIONS',
        });
        return;
      }
    } else {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'CRM.MESSAGES.HEADERS.NO_TURNOVER_EDIT_PERMISSIONS',
        bodyKey: 'CRM.MESSAGES.ERROR.NO_TURNOVER_EDIT_PERMISSIONS',
      });
      return;
    }

    this.isEditting = true;
    this.checkAnalystCompleted();
    this.form?.controls['status'].disable();
    this.form?.controls['createdDt'].disable();
    this.form?.controls['departingCompleted'].disable();
    this.form?.controls['incomingCompleted'].disable();
  }

  updateActionsList() {
    const selectAction$ = this._translateService.get('COMMON.LABEL.CHOOSE');
    const departingComplete$ = this._translateService.get(
      'CRM.LABEL.DEPARTING_COMPLETE'
    );
    const incomingComplete$ = this._translateService.get(
      'CRM.LABEL.INCOMING_COMPLETE'
    );
    const resolve$ = this._translateService.get('CRM.LABEL.RESOLVE');
    const delete$ = this._translateService.get('CRM.LABEL.DELETE');
    const print$ = this._translateService.get('COMMON.LABEL.PRINT');
    const viewAudit$ = this._translateService.get(
      'COMMON.LABEL.VIEW_AUDIT_INFO'
    );

    let select,
      dc,
      ic,
      resolve,
      del,
      print,
      audit = null;
    forkJoin([
      selectAction$,
      departingComplete$,
      incomingComplete$,
      resolve$,
      delete$,
      print$,
      viewAudit$,
    ])
      .pipe(take(1))
      .subscribe((messages) => {
        select = messages[0];
        dc = messages[1];
        ic = messages[2];
        resolve = messages[3];
        del = messages[4];
        print = messages[5];
        audit = messages[6];

        this.actions = [
          { label: select, value: null },
          { label: print, value: 'print-shift' },
        ];

        if (this.shiftTurnover?.status === 'N') {
          this.actions.push({ label: dc, value: 'departing-complete' });
        }

        if (this.shiftTurnover?.status === 'DC') {
          this.actions.push({ label: ic, value: 'incoming-complete' });
        }

        if (this.canEditResolved || this.canEditUnresolved) {
          this.actions.push({ label: del, value: DELETE_SHIFT_TURNOVER });
        }

        this.actions.push({ label: audit, value: VIEW_AUDIT });

        this.actions = [...this.actions];
      });
  }

  actionEvent($event: SelectChangeEvent, object?: any) {
    this.selectedAction = null;
    if ($event.value) {
      if ($event.value === DEPARTING_COMPLETED) {
        this.shiftTurnover?.ShiftDetails.forEach((d) => {
          if (d.incomingDepartingFlag === 'D') {
            let saveMe = true;
            const anyFalseItems = d.ShiftTurnoverItems.some(
              (i) => i.turnoverFlag === false
            );
            if (anyFalseItems === true) {
              this._logAndMessage.translateToErrorAlert({
                headerKey: 'CRM.MESSAGES.HEADERS.DEPARTING_NOT_COMPLETE',
                bodyKey: 'CRM.MESSAGES.ERROR.MISSING_ITEMS',
              });
              saveMe = false;
            }

            const mitigations = d.ShiftTurnoverMitigations
              ? d.ShiftTurnoverMitigations.filter(
                  (m) => m.turnoverFlag === true
                )
              : [];
            if (!d.fitFlag) {
              this._logAndMessage.translateToErrorAlert({
                headerKey: 'CRM.MESSAGES.HEADERS.DEPARTING_NOT_COMPLETE',
                bodyKey: 'CRM.MESSAGES.ERROR.FIT_MISSING',
              });
              saveMe = false;
            }

            if (mitigations.length < 1) {
              this._logAndMessage.translateToErrorAlert({
                headerKey: 'CRM.MESSAGES.HEADERS.DEPARTING_NOT_COMPLETE',
                bodyKey: 'CRM.MESSAGES.ERROR.CRM_MIT_MISSING',
              });
              saveMe = false;
            }
            if (saveMe === true) {
              this.previousTurnoverStatus = this.shiftTurnover!.status;
              this.shiftTurnover!.status = 'DC';
              this.saveShiftTurnover();
            }
          }
        });
      } else if ($event.value === INCOMING_COMPLETED) {
        this.shiftTurnover?.ShiftDetails.forEach((d) => {
          if (d.incomingDepartingFlag === 'I') {
            let saveMe = true;
            const anyFalseItems = d.ShiftTurnoverItems.some(
              (i) => i.turnoverFlag === false
            );
            if (anyFalseItems === true) {
              this._logAndMessage.translateToErrorAlert({
                headerKey: 'CRM.MESSAGES.HEADERS.INCOMING_NOT_COMPLETE',
                bodyKey: 'CRM.MESSAGES.ERROR.MISSING_ITEMS',
              });
              saveMe = false;
            }

            if (!d.fitFlag) {
              this._logAndMessage.translateToErrorAlert({
                headerKey: 'CRM.MESSAGES.HEADERS.INCOMING_NOT_COMPLETE',
                bodyKey: 'CRM.MESSAGES.ERROR.FIT_MISSING',
              });
              saveMe = false;
            }

            if (saveMe === true) {
              this.previousTurnoverStatus = this.shiftTurnover!.status;
              this.shiftTurnover!.status = 'R';
              this.saveShiftTurnover();
            }
          }
        });
      } else if ($event.value === PRINT_SHIFT_TURNOVER) {
        const tab = this._tabService.buildNewTab(
          MC_ST_PRINT_CONTAINER,
          true,
          undefined,
          this.shiftTurnover!.id!
        );
        this._tabService.openTab(tab);
      } else if ($event.value === VIEW_AUDIT) {
        const history$ = this._shiftTurnoverApi.getShiftTurnoverHistories(
          this.shiftTurnover!.id!
        );
        this._dialogService.open(AuditDialogContainerComponent, {
          dismissableMask: true,
          modal: true,
          closable: true,
          width: '25%',
          data: {
            history: history$,
          },
        });
      } else if ($event.value === DELETE_SHIFT_TURNOVER) {
        if (
          (this.shiftTurnover?.status !== 'R' && !this.canEditUnresolved) ||
          (this.shiftTurnover?.status == 'R' && !this.canEditResolved)
        ) {
          return;
        }
        let confHeader = null,
          ok = null,
          cancel = null,
          message = null;
        const confHeader$ = this._translateService.get(
          'CRM.MESSAGES.HEADERS.DELETE_TURNOVER'
        );
        const ok$ = this._translateService.get('COMMON.LABEL.BUTTONS.YES');
        const cancel$ = this._translateService.get('COMMON.LABEL.BUTTONS.NO');
        const message$ = this._translateService.get(
          'CRM.MESSAGES.CONFIRMATION.DELETE_TURNOVER'
        );

        forkJoin([confHeader$, ok$, cancel$, message$]).subscribe(
          (messages) => {
            confHeader = messages[0];
            ok = messages[1];
            cancel = messages[2];
            message = messages[3];
          }
        );
        this._confirmationService.confirm({
          header: confHeader!,
          icon: 'fa fa-question-circle',
          acceptVisible: true,
          rejectVisible: true,
          acceptLabel: ok!,
          rejectLabel: cancel!,
          message: message!,
          accept: () => {
            this.saving = true;
            this._shiftTurnoverApi
              .deleteShiftTurnover(this.shiftTurnover!.id!)
              .subscribe((result) => {
                this.saving = false;
                this._logAndMessage.translateToSuccessMessage({
                  bodyKey: 'COMMON.MESSAGES.SUCCESS.DELETE_SUCCESS_MSG',
                  headerKey: 'COMMON.MESSAGES.HEADERS.DELETE',
                });
                const index = this._tabService.getActiveIndex();
                this._tabService.closeTab(index);
              });
          },
          reject: () => {},
        });
      }

      if (!isNullOrUndefined(object)) {
        object.clear(null);
      }
    }
    this._cdRef.markForCheck();
  }

  contactSearch($event: AutoCompleteCompleteEvent) {
    const query = $event && $event.query ? $event.query.toLowerCase() : '';
    this._contactApi
      .getTurnoverContacts(this.shiftTurnover!.shiftGroupId)
      .subscribe((contacts) => {
        this.filteredContacts = contacts
          .sort(dynamicSort('firstName', 1))
          .filter((c) =>
            (c.firstName + ' ' + c.lastName).toLowerCase().includes(query)
          )
          .map((c) => ({
            id: c.id,
            fullName: c.firstName + ' ' + c.lastName,
          }));
      });
  }

  saveShiftTurnover() {
    if (!this.canCreate && !this.canEditUnresolved && !this.canEditResolved) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'CRM.MESSAGES.HEADERS.CREATE_TURNOVER',
        bodyKey: 'CRM.MESSAGES.ERROR.CREATE_TURNOVER_PERMS',
      });
      return;
    }

    const dv = getDirtyValues(this.form);
    this.saving = true;
    this.isEditting = false;
    if (this.shiftTurnover?.id) {
      const updateStatement = this._shiftTurnoverBuilder.buildUpdateStatement(
        this.shiftTurnover,
        dv
      );
      this.updateShiftTurnover(updateStatement);
    } else {
      const createStatement = this._shiftTurnoverBuilder.buildCreateStatement(
        this.shiftTurnover,
        dv
      );
      this.createShiftTurnover(createStatement);
    }
  }

  updateShiftTurnover(updateStatement: UpdateShiftTurnoverInput) {
    if (
      (this.previousTurnoverStatus !== 'R' && !this.canEditUnresolved) ||
      (this.previousTurnoverStatus === 'R' && !this.canEditResolved)
    ) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'CRM.MESSAGES.HEADERS.UPDATE_TURNOVER',
        bodyKey: 'CRM.MESSAGES.ERROR.UPDATE_TURNOVER_PERMS',
      });
      this._logAndMessage.errorLogOnly(
        `User doesn't have permissions to update this turnover, previous status: ${this.previousTurnoverStatus}, canEditUnresolved: ${this.canEditUnresolved}, canEditResolved: ${this.canEditResolved}, updateStatement: ${updateStatement}`
      );
      this.saving = false;
      return;
    }

    this._shiftTurnoverApi
      .updateShiftTurnover(this.shiftTurnover!.id, updateStatement)
      .pipe(take(1))
      .subscribe({
        next: (turnover) => {
          this.previousTurnoverStatus = '';
          this.handleShiftTurnoverSave(turnover);
        },
        error: (error) => {
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'CRM.MESSAGES.HEADERS.UPDATE_TURNOVER',
            bodyKey: 'CRM.MESSAGES.ERROR.UPDATE_TURNOVER',
          });
          console.error(error);
          this.saving = false;
          this.isEditting = true;
        },
      });
  }

  createShiftTurnover(updateStatement: any) {
    if (!this.canCreate) {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'CRM.MESSAGES.HEADERS.CREATE_TURNOVER',
        bodyKey: 'CRM.MESSAGES.ERROR.CREATE_TURNOVER_PERMS',
      });
      this._logAndMessage.errorLogOnly(
        "User doesn't have permissions to create turnovers"
      );
      this.saving = false;
      return;
    }
    this._shiftTurnoverApi.createShiftTurnover(updateStatement).subscribe({
      next: (turnover) => {
        this.handleShiftTurnoverSave(turnover);
      },
      error: (error) => {
        console.error(error);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          bodyKey: 'CRM.MESSAGES.ERROR.CREATE_SHIFT_TURNOVER',
        });
        this.saving = false;
      },
      complete: () => {
        const oldTab = this._tabService.getActiveTab();
        const newTab = this._tabService.buildNewTab(
          MC_ST_DETAIL_CONTAINER,
          true,
          undefined,
          this.shiftTurnover!.id!
        );
        this._tabService.replaceTab(oldTab, newTab);
      },
    });
  }

  handleShiftTurnoverSave(turnover: ShiftTurnover) {
    this._logAndMessage.translateToSuccessMessage({
      bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
      headerKey: 'COMMON.MESSAGES.HEADERS.SUBMIT',
    });
    this.shiftTurnover = turnover;
    this.saving = false;
    this.updateForm();
    this.form?.disable();
    this.updateActionsList();
    this._cdRef.markForCheck();
    this.changesEvent.emit({
      isDirty: false,
      index: this.index(),
      id: this.id,
    });
  }

  shouldShowEditSaveButtons() {
    if (
      (this.shiftTurnover?.status !== 'R' && this.canEditUnresolved) ||
      (this.shiftTurnover?.status == 'R' && this.canEditResolved)
    ) {
      return true;
    }
    return false;
  }

  refresh(): void {
    if (this.shiftTurnover && this.shiftTurnover.id) {
      this.saving = true;
      this._shiftTurnoverApi.getShiftTurnover(this.shiftTurnover.id).subscribe({
        next: (turnover) => {
          this.updatePageTurnover(turnover);
        },
        error: (error) => {
          this.saving = false;
        },
        complete: () => {
          this.saving = false;
        },
      });
    }
  }

  getChildControls(key: string): AbstractControl[] {
    const keyArray = this.form?.get(key) as FormArray;
    return keyArray.controls;
  }

  getBaseFormChild(key: string): FormControl {
    return this.form?.get(key) as FormControl;
  }

  getItemFormChild(groupIndex: number, key: string): FormControl {
    return this.form
      ?.get('items')
      ?.get(`${groupIndex}`)
      ?.get(key) as FormControl;
  }

  setupActiveTabChangedSubscription(): void {
    this.activeTabChangedSub = this._tabService.activeTabChanged.subscribe(
      (tab) => {
        if (
          !isNullOrUndefined(tab) &&
          tab.header === this.screenName &&
          !this.isEditting
        ) {
          this.refresh();
        }
      }
    );
  }

  updatePageTurnover(turnover: ShiftTurnover): void {
    this.shiftTurnover = turnover;
    this.loadShiftData();
    this.backgroundRefreshPaused = false;
    this._logAndMessage.translateToSuccessMessage({
      headerKey: 'CRM.MESSAGES.HEADERS.BACKGROUND_TURNOVER_UPDATE_SUCCESS',
      bodyKey: 'CRM.MESSAGES.SUCCESS.BACKGROUND_TURNOVER_UPDATE_SUCCESS',
    });
    this.isEditting = false;
  }

  performBackgroundRefresh(): void {
    if (!this.shiftTurnover || this.backgroundRefreshPaused) {
      // No turnover id to refresh so we can skip for now
      // or the refresh is paused due to them declining an update
      return;
    }

    this._shiftTurnoverApi.getShiftTurnover(this.shiftTurnover.id).subscribe({
      next: async (turnover) => {
        const currentPageVersion = this.shiftTurnover!.version;
        const newVersion = turnover.version;

        if (newVersion > currentPageVersion) {
          // Automatically update in the background when they aren't currently editing
          if (!this.isEditting) {
            this.updatePageTurnover(turnover);
          } else {
            // Prompt to ask for update or not
            const translations = await firstValueFrom(
              forkJoin({
                header: this._translateService.get(
                  'CRM.MESSAGES.HEADERS.BACKGROUND_TURNOVER_UPDATE'
                ),
                message: this._translateService.get(
                  'CRM.MESSAGES.CONFIRMATION.BACKGROUND_TURNOVER_UPDATE'
                ),
              })
            );
            this._confirmationService.confirm({
              header: translations.header,
              icon: 'fa fa-question-circle',
              message: translations.message,
              accept: () => {
                this.updatePageTurnover(turnover);
              },
              reject: () => {
                this.backgroundRefreshPaused = true;
              },
            });
          }
        }
      },
      error: (err) => {
        console.warn('Failed background refresh for this turnover', err);
      },
    });
  }
}
